import type { Params as GetSignedUploadParams } from '@api/routes/asset/get-upload-url/params';
import React from 'react';
import { getSignedUploadUrl, uploadMedia } from 'src/store/api/assets';

import { _validateImage, _validateVideo } from './helpers';

interface UseMediaUploadProps {
  type: 'images' | 'videos';
  onError: (err: string) => void;
  onUploadComplete: (url: string, gcpTempPath: string, fileName: string) => void;
}

export const useMediaUpload = ({ type, onError, onUploadComplete }: UseMediaUploadProps) => {
  const [uploadProgress, setUploadProgress] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);
  const [uploadedUrl, setUploadedUrl] = React.useState('');

  const uploadFile = React.useCallback(
    async (file: File) => {
      setUploadProgress(0);
      setUploadedUrl('');
      setIsLoading(true);

      const errors = type === 'videos' ? await _validateVideo(file) : await _validateImage(file);

      if (errors?.length) {
        onError(errors[0]);
        setIsLoading(false);

        return { success: false };
      }

      const ext = (file.name.split('.').pop()?.toLowerCase() ?? 'jpg') as GetSignedUploadParams['extension'];

      const signedUrlResult = await getSignedUploadUrl({
        extension: ext,
      });

      if (!signedUrlResult.success) {
        onError('UPLOAD_FAILED');
        setIsLoading(false);

        return { success: false };
      }

      const result = await uploadMedia(signedUrlResult.data.signedUrl, file, progress => {
        setUploadProgress(progress);
      });

      if (!result.success) {
        onError('UPLOAD_FAILED');
        setIsLoading(false);

        return { success: false };
      }

      const { publicUrl, gcpTempPath } = signedUrlResult.data;

      setUploadedUrl(publicUrl);
      setIsLoading(false);

      onUploadComplete(publicUrl, gcpTempPath, file.name);

      return {
        success: true,
        url: publicUrl,
      };
    },
    [onUploadComplete, type]
  );

  return {
    uploadFile,
    uploadProgress,
    isLoading,
    uploadedUrl,
    onError,
  };
};
